/**
 * <keystone-field-number> — numeric input.
 *
 * Maps onto JSON Schema `type: "number"` (decimals) and `type: "integer"`
 * (whole numbers). Caller sets `integer="true"` for integers; we use that
 * to set `step=1` and reject decimal input at parse time.
 *
 * The internal `value` is always stored as a string so empty / partial
 * input states round-trip cleanly. The <keystone-form> coerces to number
 * at submit time based on the schema type.
 */

import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

@customElement("keystone-field-number")
export class KeystoneFieldNumber extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  @property({ type: Boolean }) integer = false;
  @property({ type: Number }) min: number | null = null;
  @property({ type: Number }) max: number | null = null;

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: input.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;
    const step = this.integer ? "1" : "any";

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <input
        id=${inputId}
        name=${this.name}
        type="number"
        .value=${this.value}
        ?required=${this.required}
        ?disabled=${this.disabled}
        min=${ifDefined(this.min ?? undefined)}
        max=${ifDefined(this.max ?? undefined)}
        step=${step}
        inputmode=${this.integer ? "numeric" : "decimal"}
        aria-invalid=${this.error ? "true" : "false"}
        aria-describedby=${this.error ? `${inputId}-error` : nothing}
        @input=${this.onInput}
      />
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-number": KeystoneFieldNumber;
  }
}
